import { ReactNode, RefObject, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Dog, GiftIcon } from "lucide-react";
import toast from "react-hot-toast";
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ShoppingCartIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { ICartDetailsResponseData, ICartGiftCard, ICartSearchResponseData } from "@/api_framework/api_modals/Cart";
import CircleSpinnerLoader from "@/app/components/ui/Reusable/LoaderComponent/CircleSpinnerLoader";
import PlanCard from "@/app/components/routes/auth_routes/CartManagement/BillingMirrorComponent/MemberShipComponent/MembershipPlans/PlanCard";
import { mapPitchingCards } from "@/app/components/routes/auth_routes/CartManagement/BillingMirrorComponent/MemberShipComponent/MembershipPlans";
import { useMirrorMembershipSimulationAPI } from "@/app/hooks/api_hooks/Cart/useMirrorMembershipSimulationAPI";
import { useMembershipPitchCardsAPI } from "@/app/hooks/api_hooks/Cart/useMembershipPitchCardsAPI";
import { useCartGiftsAPI } from "@/app/hooks/api_hooks/Cart/useCartGiftsAPI";
import { useSocket } from "@/app/components/socket/SocketProvider";
import { MEMBERSHIP_SIMULATION_EVENTS } from "@/app/components/routes/auth_routes/CartManagement/BillingMirrorComponent/types";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { useAppSelector } from "@/app_redux/hooks/root_hook";

const SCROLL_STEP = 240;
const CONTROL_CENTER_TABS = ["All", "Membership", "Gifts", "Comments"];

const PLAN_CARD_ZOOM_CLASS = [
  "[zoom:0.3] [&>div]:!w-[580px]",
  "[@container(min-width:230px)]:[zoom:0.35]",
  "[@container(min-width:259px)]:[zoom:0.4]",
  "[@container(min-width:288px)]:[zoom:0.45]",
  "[@container(min-width:317px)]:[zoom:0.5]",
  "[@container(min-width:346px)]:[zoom:0.55]",
  "[@container(min-width:375px)]:[zoom:0.6]",
  "[@container(min-width:433px)]:[zoom:0.7]",
  "[@container(min-width:491px)]:[zoom:0.8]",
  "[@container(min-width:607px)]:[zoom:1]",
].join(" ");

const TABLE_COLUMNS = "grid grid-cols-[minmax(0,1fr)_100px_180px_260px_260px] items-center gap-[16px] px-[24px]";

const ICON_BUTTON_CLASS =
  "inline-flex h-[48px] w-[48px] items-center justify-center rounded-[12px] border border-blue-200 bg-white text-blue-600 shadow-sm hover:bg-blue-50 active:scale-95 transition";

const SCROLLBAR_CLASS =
  "[&::-webkit-scrollbar]:w-[8px] [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-[#f3f4f6] [&::-webkit-scrollbar-track]:shadow-[inset_0_0_3px_rgba(0,0,0,0.15)] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#9ca3af] [&::-webkit-scrollbar-thumb]:shadow-[0_0_3px_rgba(0,0,0,0.3)]";

const getGiftKey = (gift: ICartGiftCard) => `${gift.gift_id}-${gift.reference_id}-${gift.pet_id}`;

const formatPrice = (amount: number | null | undefined) => (amount == null ? "-" : `₹${Math.trunc(Number(amount))}`);

const PriceCell = ({
  amount,
  originalAmount,
  className = "",
}: {
  amount: number | null;
  originalAmount?: number | null;
  className?: string;
}) => (
  <div className="flex flex-col items-end font-monoNums tabular-nums whitespace-nowrap">
    <span className={`text-[22px] font-bold ${className}`}>{formatPrice(amount)}</span>
    {amount != null && Number(originalAmount) > Number(amount) && (
      <span className="text-[18px] text-[#838386] line-through">{formatPrice(originalAmount)}</span>
    )}
  </div>
);

interface MembershipSimulationPopupProps {
  simulation: any;
  scrollRef?: RefObject<HTMLDivElement>;
  isAddingToCart?: boolean;
  onClose?: () => void;
  onScroll?: (delta: number) => void;
  onAddToCart?: () => void;
}

export const MembershipSimulationPopup = ({
  simulation,
  scrollRef,
  isAddingToCart = false,
  onClose,
  onScroll,
  onAddToCart,
}: MembershipSimulationPopupProps) => {
  const isCartScreen = !!onClose;
  const headingTextClass = isCartScreen ? "!text-[20px]" : "!text-[20px]";
  const membershipPriceWithoutDiscount = simulation?.membership_price_without_discount;
  const membershipPriceWithDiscount = simulation?.membership_price_with_discount ?? membershipPriceWithoutDiscount;
  const hasMembershipDiscount = Number(membershipPriceWithoutDiscount) > Number(membershipPriceWithDiscount);
  const cartGroups = [
    ...(simulation?.pets ?? []).map((pet: any) => ({
      key: pet?.pet_id,
      title: `${pet?.pet_name ?? ""}'s Cart`,
      icon: pet?.pet_species === "Cat" ? <img src="/cat.svg" alt="" /> : <Dog color="#f05b72" />,
      isHomeDelivery: false,
      items: pet?.items ?? [],
    })),
    {
      key: "home-delivery",
      title: "Home Delivery",
      icon: <img src="/delivery.svg" alt="" />,
      isHomeDelivery: true,
      items: simulation?.hd_cart?.main_cart_items ?? [],
    },
  ].filter((cartGroup) => cartGroup.items.length > 0);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        onClick={(event) => event.stopPropagation()}
        className={`relative w-full max-w-[1580px] max-h-[800px] rounded-[40px] overflow-hidden flex flex-col font-roboto
          shadow-[4px_8px_32px_0px_rgba(0,0,0,0.05)] bg-white
          bg-[url('/background_membership.svg')] bg-[length:110%_auto] bg-center ${isCartScreen ? "[zoom:0.8]" : ""}`}
      >
        <div className="flex-1 min-h-0 flex flex-col px-[75px] pt-[16px] pb-[35px] leading-[normal]">
          {isCartScreen && (
            <div className="flex justify-end gap-[12px] shrink-0">
              <button
                type="button"
                aria-label="Scroll up"
                onClick={() => onScroll?.(-SCROLL_STEP)}
                className={ICON_BUTTON_CLASS}
              >
                <ChevronUpIcon className="h-[24px] w-[24px]" />
              </button>
              <button
                type="button"
                aria-label="Scroll down"
                onClick={() => onScroll?.(SCROLL_STEP)}
                className={ICON_BUTTON_CLASS}
              >
                <ChevronDownIcon className="h-[24px] w-[24px]" />
              </button>
              <button type="button" aria-label="Close simulation" onClick={onClose} className={ICON_BUTTON_CLASS}>
                <XMarkIcon className="h-[28px] w-[28px]" />
              </button>
            </div>
          )}

          <div ref={scrollRef} className={`flex-1 min-h-0 mt-[16px] overflow-y-auto pr-[8px] ${SCROLLBAR_CLASS}`}>
            {cartGroups.map((cartGroup) => (
              <div key={cartGroup.key} className="mt-[16px]">
                <div className="mb-[12px] flex items-center gap-[12px]">
                  {cartGroup.icon}
                  <p className="text-[22px] font-medium uppercase text-black">{cartGroup.title}</p>
                  {/* {cartGroup.isHomeDelivery && (
                    <span className="bg-[#FFDAE0] text-[#F05B72] px-[7px] py-[5px] text-[16px] font-medium rounded-tl-[20px] rounded-br-[20px] whitespace-nowrap">
                      Delivery in 2 hours
                    </span>
                  )} */}
                </div>
                <div className="bg-white border border-[#e3e8ef] rounded-[20px] overflow-hidden">
                  <div className={`${TABLE_COLUMNS} bg-[#f7f5f2] py-[16px] font-medium text-black`}>
                    <p className={headingTextClass}>ITEM</p>
                    <p className={`text-center ${headingTextClass}`}>QTY</p>
                    <p className={`text-right ${headingTextClass}`}>UNIT PRICE</p>
                    <p className={`text-right ${headingTextClass}`}>WITHOUT MEMBERSHIP</p>
                    <p
                      className={`-my-[16px] py-[16px] self-stretch -mr-[24px] flex items-center justify-end pr-[24px] bg-gradient-to-l from-[#e3f7da] via-[#f5fcf2] via-40% to-transparent ${headingTextClass}`}
                    >
                      WITH MEMBERSHIP
                    </p>
                  </div>
                  {cartGroup.items.map((lineItem: any, index: number) => (
                    <div
                      key={lineItem?.unique_row_id ?? index}
                      className={`${TABLE_COLUMNS} py-[14px] ${index < cartGroup.items.length - 1 ? "border-b border-[#e3e8ef]" : ""}`}
                    >
                      <div className="flex min-w-0 items-center gap-[10px]">
                        {(lineItem?.tag === "#membership" || lineItem?.is_membership_discount_applied) && (
                          <img src="/membership_crown.svg" alt="" className="h-[24px] w-[24px] shrink-0" />
                        )}
                        <p className="truncate font-semibold text-[22px] text-black" title={lineItem?.name}>
                          {lineItem?.name}
                        </p>
                      </div>
                      <p className="text-center text-[22px] font-monoNums">{lineItem?.qty ?? "-"}</p>
                      <p className="text-right text-[22px] font-monoNums tabular-nums">
                        {formatPrice(lineItem?.unit_price)}
                      </p>
                      <PriceCell
                        amount={lineItem?.discounted_price_without_membership}
                        originalAmount={lineItem?.price_without_membership}
                      />
                      <div className="-my-[14px] py-[14px] self-stretch -mr-[24px] flex items-center justify-end pr-[24px] bg-gradient-to-l from-[#e3f7da] via-[#f5fcf2] via-40% to-transparent">
                        <PriceCell
                          amount={lineItem?.discounted_price_with_membership}
                          originalAmount={lineItem?.price_with_membership}
                          className="text-[#30820d]"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div
            className={`${TABLE_COLUMNS} relative isolate overflow-hidden shrink-0 mt-[24px] py-[20px] bg-gradient-to-r from-[#e6ffdc] via-white via-45% to-white border border-[#409c18] rounded-[20px]`}
          >
            <div className="pointer-events-none absolute inset-y-0 right-0 w-[284px] -z-10 bg-gradient-to-l from-[#e3f7da] via-[#f5fcf2] via-40% to-transparent" />
            <p className="col-span-3 text-[24px] font-semibold text-black">
              Buy membership to <span className="text-[#30820d]">save {formatPrice(simulation?.savings_on_cart)}</span>{" "}
              in this cart
            </p>
            <PriceCell amount={simulation?.total_price_without_membership} />
            <PriceCell amount={simulation?.total_price_with_membership} className="text-[#30820d]" />
          </div>

          <div className="shrink-0 mt-[16px] flex items-center justify-between gap-[24px] rounded-[20px] border border-[#e0b869] bg-gradient-to-r from-[#faeacb] via-[#fffaf0] to-[#faeacb] px-[24px] py-[8px]">
            <div className="flex min-w-0 items-center gap-[12px]">
              <img src="/membership_crown.svg" alt="" className="h-[32px] w-[32px] shrink-0" />
              <p className="truncate text-[22px] font-semibold text-[#7a5518]" title={simulation?.membership_name}>
                {simulation?.membership_name}
              </p>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-[4px] font-monoNums tabular-nums">
              <div className="flex items-center gap-[10px]">
                {simulation?.membership_discount_label && (
                  <span className="rounded-[8px] border border-[#409c18] bg-[#e6ffdc] px-[8px] py-[2px] text-[14px] font-semibold text-[#30820d] whitespace-nowrap">
                    {simulation?.membership_discount_label}
                  </span>
                )}
                <span className="text-[24px] font-bold text-black">{formatPrice(membershipPriceWithDiscount)}</span>
              </div>
              {hasMembershipDiscount && (
                <span className="text-[20px] font-medium text-[#838386] line-through decoration-[1.5px]">
                  {formatPrice(membershipPriceWithoutDiscount)}
                </span>
              )}
            </div>
          </div>

          {onAddToCart && (
            <div className="shrink-0 mt-[20px] flex justify-end">
              <button
                type="button"
                disabled={isAddingToCart}
                onClick={onAddToCart}
                className="inline-flex h-[52px] min-w-[200px] items-center justify-center gap-[10px] rounded-[12px] bg-[#07863d] px-[28px] text-[20px] font-semibold text-white shadow-[0_6px_16px_rgba(7,134,61,0.25)] transition hover:bg-[#066f33] active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isAddingToCart ? (
                  <CircleSpinnerLoader h="24" w="24" />
                ) : (
                  <>
                    <ShoppingCartIcon className="h-[24px] w-[24px]" />
                    Add to Cart
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface CartControlCenterProps {
  cartId: string;
  clinicId: string;
  customerId: string;
  addItemToCart: (
    item: ICartSearchResponseData,
    swap?: boolean,
    applyMembershipCoupons?: boolean,
  ) => Promise<any> | void;
  commentsContent: ReactNode;
  // The cart's pet lines: gift cards flip between Add / added as these change
  cartPets?: ICartDetailsResponseData["pets"];
}

const CartControlCenter = ({
  cartId,
  clinicId,
  customerId,
  addItemToCart,
  commentsContent,
  cartPets,
}: CartControlCenterProps) => {
  const { execute: logAPI } = useClickLogAPI();
  const userEmailId = useAppSelector((state) => state.auth?.user?.email);
  const mappedClinicName = useAppSelector((state) => state.root.UIStates.mappedClinicName);
  const fetchMembershipSimulation = useMirrorMembershipSimulationAPI();
  const fetchPitchCards = useMembershipPitchCardsAPI();
  const fetchGifts = useCartGiftsAPI();
  const { sendJsonMessage, lastJsonMessage } = useSocket();
  const [activeTab, setActiveTab] = useState(CONTROL_CENTER_TABS[0]);
  const [pitchingCards, setPitchingCards] = useState<any[]>([]);
  const [isLoadingPitchingCards, setIsLoadingPitchingCards] = useState(false);
  const [simulation, setSimulation] = useState<any | null>(null);
  const [simulatingMembershipId, setSimulatingMembershipId] = useState<string | null>(null);
  const [addingMembershipId, setAddingMembershipId] = useState<string | null>(null);
  const [gifts, setGifts] = useState<ICartGiftCard[]>([]);
  const [isLoadingGifts, setIsLoadingGifts] = useState(false);
  const [giftsRefreshKey, setGiftsRefreshKey] = useState(0);
  const [addingGiftKey, setAddingGiftKey] = useState<string | null>(null);
  const simulationScrollRef = useRef<HTMLDivElement>(null);
  const isBusy = simulatingMembershipId !== null || addingMembershipId !== null;
  // Changes when a line is added/removed or a line's coupon changes (a gift added onto an existing service line)
  const cartLinesKey = useMemo(
    () =>
      (cartPets ?? [])
        .flatMap((pet: any) => (pet?.items ?? []).map((item: any) => `${item?.unique_row_id}:${item?.coupon_id ?? ""}`))
        .join("|"),
    [cartPets],
  );

  const logControlCenter = (event_type: string, extra: Partial<IClickLogBody> = {}) => {
    logAPI({
      event_type,
      scr_id: "CART_CONTROL_CENTER",
      cart_id: cartId,
      client_id: customerId,
      clinic_id: clinicId,
      clinic_name: mappedClinicName,
      vms_user_id: userEmailId,
      timestamp: Date.now(),
      ...extra,
    });
  };

  const getPlanLog = (membershipId: string) => {
    const card = pitchingCards.find((pitchingCard) => String(pitchingCard?.membership_id) === String(membershipId));
    return {
      membership_id: membershipId,
      membership_name: card?.category ?? "",
      membership_tag: card?.is_top_best_match
        ? "BEST_MATCH"
        : card?.is_renewal && card?.renew_now
          ? "RENEW_NOW"
          : card?.is_recommended
            ? "RECOMMENDED"
            : "",
    };
  };

  // Initial load: pitch-cards returns every card, renewals included (the mirror's membership_cards drops shown renewals).
  const loadPitchingCards = () =>
    fetchPitchCards(cartId).then((response) => {
      const cards = Array.isArray(response?.data) ? response.data : [];
      setPitchingCards(cards);
      return cards;
    });

  useEffect(() => {
    if (!cartId || !clinicId) return;
    setIsLoadingPitchingCards(true);
    loadPitchingCards()
      .then((cards) => {
        logControlCenter("CC_MEMBERSHIP_PLANS_VIEWED", {
          items_count: cards.length,
          membership_details: cards.map((card: any) => ({
            membership_id: card?.membership_id,
            membership_name: card?.category,
            price_per_year: card?.price_per_year,
            discount_label: card?.membership_discount_label ?? null,
            savings_on_this_cart: card?.savings?.savings_on_this_cart ?? null,
          })),
        });
      })
      .finally(() => setIsLoadingPitchingCards(false));
  }, [cartId, clinicId]);

  useEffect(() => {
    const socketMessage: any = lastJsonMessage;
    const eventType = socketMessage?.event ?? socketMessage?.type ?? socketMessage?.event_type;
    if (String(eventType ?? "").toLowerCase() !== "membership") return;
    if (socketMessage.cart_id && String(socketMessage.cart_id) !== String(cartId)) return;
    // The event carries the full pitch list (renewals included); fall back to the API for older payloads.
    if (Array.isArray(socketMessage.pitching_cards)) setPitchingCards(socketMessage.pitching_cards);
    else loadPitchingCards();
  }, [lastJsonMessage, cartId]);

  useEffect(() => {
    setGifts([]);
  }, [cartId]);

  // Refetched on every cart line change, so a gift added here (or removed from the cart table) updates its card, and
  // on Refresh, for a gift the customer claims in the app meanwhile
  useEffect(() => {
    if (!cartId || !clinicId) return;
    let isStale = false;
    setIsLoadingGifts(true);
    fetchGifts(cartId)
      .then((response) => {
        if (!isStale) setGifts(response?.status && Array.isArray(response.data) ? response.data : []);
      })
      .finally(() => {
        if (!isStale) setIsLoadingGifts(false);
      });
    return () => {
      isStale = true;
    };
  }, [cartId, clinicId, cartLinesKey, giftsRefreshKey]);

  const handleRefreshGifts = () => {
    logControlCenter("CC_GIFT_REFRESH_CLICK", { cta_text: "Refresh" });
    setGiftsRefreshKey((key) => key + 1);
  };

  const sendSimulationEvent = (eventType: string, payload: Record<string, unknown> = {}) => {
    sendJsonMessage({ event_type: eventType, payload: { cart_id: cartId, ...payload } });
  };

  const handleTabClick = (tab: string) => {
    setActiveTab(tab);
    logControlCenter("CC_TAB_CLICK", { cta_text: tab });
  };

  const handleSimulate = (membershipId: string) => {
    setSimulatingMembershipId(membershipId);
    logControlCenter("CC_MEMBERSHIP_SIMULATE_CLICK", getPlanLog(membershipId));
    sendSimulationEvent(MEMBERSHIP_SIMULATION_EVENTS.OPEN, { membership_id: membershipId });
    fetchMembershipSimulation(cartId, membershipId)
      .then(({ status, data }) => {
        if (status && data) {
          setSimulation(data);
        } else {
          toast.error("Unable to simulate this membership.");
        }
        logControlCenter("CC_MEMBERSHIP_SIMULATION_RESULT", {
          ...getPlanLog(membershipId),
          event_action: status && data ? "success" : "failed",
          membership_details:
            status && data
              ? {
                  savings_on_cart: data?.savings_on_cart,
                  total_price_without_membership: data?.total_price_without_membership,
                  total_price_with_membership: data?.total_price_with_membership,
                  membership_price_with_discount: data?.membership_price_with_discount,
                }
              : undefined,
        });
      })
      .finally(() => setSimulatingMembershipId(null));
  };

  const handleCloseSimulation = (source = "close_button") => {
    logControlCenter("CC_MEMBERSHIP_SIMULATION_CLOSE", { ...getPlanLog(simulation?.membership_id), source });
    setSimulation(null);
    sendSimulationEvent(MEMBERSHIP_SIMULATION_EVENTS.CLOSE);
  };

  const handleScrollSimulation = (delta: number, source = "button") => {
    simulationScrollRef.current?.scrollBy({ top: delta, behavior: "smooth" });
    logControlCenter("CC_MEMBERSHIP_SIMULATION_SCROLL", {
      membership_id: simulation?.membership_id,
      direction: delta < 0 ? "up" : "down",
      source,
    });
    sendSimulationEvent(MEMBERSHIP_SIMULATION_EVENTS.SCROLL, { delta });
  };

  const handleAddToCart = (membershipId: string, membershipName: string, source: string) => {
    setAddingMembershipId(membershipId);
    logControlCenter("CC_MEMBERSHIP_ADD_TO_CART_CLICK", { ...getPlanLog(membershipId), source });
    return Promise.resolve(
      addItemToCart({ id: membershipId, name: membershipName, tag: "#membership" }, false, true),
    ).finally(() => setAddingMembershipId(null));
  };

  const handleAddGift = (gift: ICartGiftCard) => {
    const giftKey = getGiftKey(gift);
    setAddingGiftKey(giftKey);
    logControlCenter("CC_GIFT_ADD_TO_CART_CLICK", {
      gift_id: gift.gift_id,
      reference_id: gift.reference_id,
      pet_id: gift.pet_id,
      cta_text: gift.heading,
    });
    // item-search-click for the gift's item (selected_item as the API sends it) with gift_id + reference_id; the cart
    // refetch that follows reloads the cards
    return Promise.resolve(
      addItemToCart({
        id: gift.product_id,
        name: gift.heading,
        tag: gift.selected_item || "#service",
        pet_id: gift.pet_id,
        gift_id: gift.gift_id,
        reference_id: gift.reference_id,
      }),
    ).finally(() => setAddingGiftKey(null));
  };

  useEffect(() => {
    if (!simulation) return;
    const handleArrowKeys = (event: KeyboardEvent) => {
      if (["INPUT", "TEXTAREA"].includes((event.target as HTMLElement)?.tagName)) return;
      if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
      event.preventDefault();
      handleScrollSimulation(event.key === "ArrowUp" ? -SCROLL_STEP : SCROLL_STEP, "keyboard");
    };
    window.addEventListener("keydown", handleArrowKeys);
    return () => window.removeEventListener("keydown", handleArrowKeys);
  }, [simulation, cartId]);

  const pitchingPlans = mapPitchingCards(pitchingCards);

  const panelContent = isLoadingPitchingCards ? (
    <div className="flex flex-col gap-3">
      {[0, 1, 2].map((index) => (
        <div
          key={index}
          className="flex flex-col gap-1 rounded-[12px] border border-[#ececec] bg-[#f9fafb] p-1 animate-pulse"
        >
          <div className="flex flex-col gap-2.5 rounded-[10px] bg-white p-3">
            <div className="flex items-center justify-between gap-3 border-b border-gray-100 pb-2.5">
              <div className="h-3.5 w-2/5 rounded bg-gray-200" />
              <div className="h-3.5 w-1/4 rounded bg-gray-200" />
            </div>
            <div className="h-2.5 w-3/4 rounded bg-gray-200" />
            <div className="h-2.5 w-1/2 rounded bg-gray-200" />
            <div className="h-2.5 w-2/3 rounded bg-gray-200" />
          </div>
          <div className="flex gap-1.5">
            <div className="h-7 flex-1 rounded-lg bg-gray-200" />
            <div className="h-7 flex-1 rounded-lg bg-gray-200" />
          </div>
        </div>
      ))}
    </div>
  ) : pitchingPlans.length > 0 ? (
    <div className="flex flex-col gap-3">
      {pitchingPlans.map((plan) => (
        <div
          key={plan.id}
          className="mx-auto flex w-fit max-w-full flex-col items-center gap-1 rounded-[12px] border border-[#ececec] bg-[#f9fafb] p-1"
        >
          <div className={PLAN_CARD_ZOOM_CLASS}>
            <PlanCard plan={plan} />
          </div>
          <div className="flex w-full gap-1.5">
            <button
              type="button"
              disabled={isBusy}
              onClick={() => handleSimulate(plan.id)}
              className="flex-1 flex justify-center items-center whitespace-nowrap rounded-lg px-2 py-1.5 text-[11px] [@container(min-width:300px)]:text-xs font-semibold border border-[#0069fe] text-[#0069fe] bg-white disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {simulatingMembershipId === plan.id ? <CircleSpinnerLoader h="16" w="16" /> : "Simulate"}
            </button>
            <button
              type="button"
              disabled={isBusy}
              onClick={() => handleAddToCart(plan.id, plan.title, "plan_card")}
              className="flex-1 flex justify-center items-center whitespace-nowrap rounded-lg px-2 py-1.5 text-[11px] [@container(min-width:300px)]:text-xs font-semibold text-white bg-[#07863d] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {addingMembershipId === plan.id ? <CircleSpinnerLoader h="16" w="16" /> : "Add to Cart"}
            </button>
          </div>
        </div>
      ))}
    </div>
  ) : (
    <div className="px-1.5 py-1 text-[#6d717f] text-xs">No membership plans to pitch for this cart.</div>
  );

  const giftsHeader = (
    <div className="mb-2 flex items-center justify-between gap-2 px-1.5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-[#475467]">Gifts</p>
      <button
        type="button"
        disabled={isLoadingGifts}
        onClick={handleRefreshGifts}
        title="Refresh gifts"
        className="flex items-center gap-1 rounded-md border border-[#d0d5dd] bg-white px-1.5 py-0.5 text-[11px] font-medium text-[#475467] disabled:cursor-not-allowed disabled:opacity-60"
      >
        <ArrowPathIcon className={`h-3.5 w-3.5 ${isLoadingGifts ? "animate-spin" : ""}`} />
        Refresh
      </button>
    </div>
  );

  const giftsContent =
    isLoadingGifts && gifts.length === 0 ? (
      <div className="flex flex-col gap-3">
        {[0, 1].map((index) => (
          <div key={index} className="flex gap-2.5 rounded-[12px] border border-[#ececec] bg-white p-3 animate-pulse">
            <div className="h-10 w-10 shrink-0 rounded-lg bg-gray-200" />
            <div className="flex flex-1 flex-col gap-2">
              <div className="h-3 w-3/5 rounded bg-gray-200" />
              <div className="h-2.5 w-4/5 rounded bg-gray-200" />
              <div className="h-6 w-full rounded-lg bg-gray-200" />
            </div>
          </div>
        ))}
      </div>
    ) : gifts.length > 0 ? (
      <div className="flex flex-col gap-3">
        {gifts.map((gift) => {
          const giftKey = getGiftKey(gift);
          const isClaimed = gift.status === "CLAIMED";
          // Same card colours as the check-in modal's gifts (BookingGiftsSection)
          const statusColorClassName = isClaimed ? "bg-[#e6f7e0] text-[#3b7c3e]" : "bg-[#fee4e2] text-[#c4320a]";
          return (
            <div
              key={giftKey}
              className="flex flex-col gap-1.5 rounded-[12px] border border-[#ececec] bg-[#fbfaf7] p-1.5"
            >
              <div className="flex items-center gap-2.5 rounded-[10px] bg-white p-2.5 shadow-[0_1px_3px_rgba(16,24,40,0.1)]">
                {gift.gift_image ? (
                  <img src={gift.gift_image} alt="" className="h-10 w-10 shrink-0 rounded-lg object-cover" />
                ) : (
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#FFEEF8] text-[#f05b72]">
                    <GiftIcon size={18} />
                  </div>
                )}
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-[12px] font-semibold leading-4 text-[#101828]">{gift.heading}</p>
                    <span
                      className={`shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${statusColorClassName}`}
                    >
                      {isClaimed ? "Claimed" : "Eligible"}
                    </span>
                  </div>
                  {(gift.sub_heading || gift.gift_mrp != null) && (
                    <p className="flex flex-wrap items-baseline gap-x-1.5 text-[11px] leading-4 text-[#475467]">
                      {gift.sub_heading && <span>{gift.sub_heading}</span>}
                      {gift.gift_mrp != null && (
                        <span className="text-[#98a2b3] line-through">{formatPrice(gift.gift_mrp)}</span>
                      )}
                    </p>
                  )}
                </div>
              </div>
              {gift.cta?.action === "add" && gift.cta.is_actionable ? (
                <button
                  type="button"
                  disabled={addingGiftKey !== null}
                  onClick={() => handleAddGift(gift)}
                  className="flex w-full justify-center items-center whitespace-nowrap rounded-lg px-2 py-1.5 text-[12px] font-semibold text-white bg-[#07863d] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {addingGiftKey === giftKey ? <CircleSpinnerLoader h="16" w="16" /> : gift.cta.text || "Add"}
                </button>
              ) : gift.cta?.action === "remove" ? (
                <div className="flex w-full items-center justify-center gap-1 rounded-lg bg-[#e6f7e0] px-2 py-1.5 text-[12px] font-semibold text-[#3b7c3e]">
                  <CheckCircleIcon className="h-4 w-4" /> Added to cart
                </div>
              ) : (
                <div
                  className={`w-full rounded-lg px-2 py-1.5 text-center text-[12px] font-semibold ${statusColorClassName}`}
                >
                  {gift.cta?.text || "Ask to claim in app"}
                </div>
              )}
            </div>
          );
        })}
      </div>
    ) : (
      <div className="px-1.5 py-1 text-[#6d717f] text-xs">No gifts available for this cart.</div>
    );

  return (
    <div className="mt-2 flex min-h-[280px] flex-1 flex-col overflow-hidden rounded-xl bg-white shadow-md [container-type:inline-size] cart-print-hide">
      <div className="flex shrink-0 items-center bg-[linear-gradient(90deg,#FFC9DC_0%,#FFEEF8_100%)] px-2.5 py-2">
        <h3 className="text-[13px] font-semibold [@container(min-width:300px)]:text-[14px]">Control Center</h3>
      </div>
      <div className="flex shrink-0 flex-wrap gap-1.5 border-b border-gray-100 px-2 py-2">
        {CONTROL_CENTER_TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => handleTabClick(tab)}
            className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] [@container(min-width:300px)]:px-2.5 [@container(min-width:300px)]:text-xs font-medium border ${
              activeTab === tab
                ? "border-[#0069fe] bg-[#0069fe] text-white"
                : "border-[#d0d5dd] bg-white text-[#475467]"
            }`}
          >
            {tab}
            {/* Shown inside the pill, after the label, only while the gifts API has returned at least one gift for this cart */}
            {tab === "Gifts" && gifts.length > 0 && (
              <span aria-label="Gifts available" className="relative flex h-2 w-2 shrink-0">
                {/* The pulse starts wider than the dot (12px vs 8px) and fully opaque, so it shows on a small tab */}
                <span className="absolute -inset-0.5 inline-flex animate-ping rounded-full bg-red-500" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-red-600 ring-2 ring-white" />
              </span>
            )}
          </button>
        ))}
      </div>
      <div
        className={`min-h-0 flex-1 overflow-y-auto overscroll-contain overflow-x-hidden break-words px-1 py-2 ${SCROLLBAR_CLASS}`}
      >
        {activeTab === "All" ? (
          <div className="flex flex-col gap-4">
            <div>
              <p className="mb-2 px-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#475467]">Membership</p>
              {panelContent}
            </div>
            {(isLoadingGifts || gifts.length > 0) && (
              <div className="border-t border-gray-100 pt-3">
                {giftsHeader}
                {giftsContent}
              </div>
            )}
            <div className="border-t border-gray-100 pt-3">
              <p className="mb-2 px-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#475467]">Comments</p>
              {commentsContent}
            </div>
          </div>
        ) : activeTab === "Comments" ? (
          commentsContent
        ) : activeTab === "Gifts" ? (
          <>
            {giftsHeader}
            {giftsContent}
          </>
        ) : (
          panelContent
        )}
      </div>
      {simulation &&
        createPortal(
          <MembershipSimulationPopup
            simulation={simulation}
            scrollRef={simulationScrollRef}
            isAddingToCart={addingMembershipId === simulation.membership_id}
            onClose={() => handleCloseSimulation()}
            onScroll={handleScrollSimulation}
            onAddToCart={() =>
              handleAddToCart(simulation.membership_id, simulation.membership_name, "simulation_popup").then(() =>
                handleCloseSimulation("add_to_cart"),
              )
            }
          />,
          document.body,
        )}
    </div>
  );
};

export default CartControlCenter;
